import { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  IconButton,
  Typography,
  Paper,
  Chip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  StructuredQuestion,
  QUESTION_TYPES,
} from '../interfaces/questions'

interface CompactQuestionEditorProps {
  question: StructuredQuestion
  onChange: (updated: StructuredQuestion) => void
  disabled?: boolean
}

/**
 * A narrow-width question editor for the diff result column.
 * Vertically stacked, size="small" throughout, no conditional/dependency editing.
 */
export default function CompactQuestionEditor({
  question,
  onChange,
  disabled = false,
}: CompactQuestionEditorProps) {
  const [localQuestion, setLocalQuestion] =
    useState<StructuredQuestion>(question)
  const [optionInput, setOptionInput] = useState('')

  useEffect(() => {
    setLocalQuestion(question)
  }, [question])

  const handleUpdate = (updates: Partial<StructuredQuestion>) => {
    const updated = { ...localQuestion, ...updates }
    setLocalQuestion(updated)
    onChange(updated)
  }

  const handleAddOption = () => {
    if (!optionInput.trim()) return
    const newOptions = [...(localQuestion.options || []), optionInput.trim()]
    handleUpdate({ options: newOptions })
    setOptionInput('')
  }

  const handleRemoveOption = (index: number) => {
    const newOptions =
      localQuestion.options?.filter((_, i) => i !== index) || []
    handleUpdate({ options: newOptions })
  }

  const isChoiceType =
    localQuestion.question_type === 'single_choice' ||
    localQuestion.question_type === 'multi_choice'

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      {/* Row 1: Question Number + Type */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        <TextField
          label="Q#"
          value={localQuestion.question_number}
          onChange={(e) => handleUpdate({ question_number: e.target.value })}
          size="small"
          sx={{ width: 70 }}
        />
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={localQuestion.question_type}
            label="Type"
            onChange={(e) => handleUpdate({ question_type: e.target.value })}
          >
            {QUESTION_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Section */}
      <TextField
        label="Section"
        value={localQuestion.section || ''}
        onChange={(e) => handleUpdate({ section: e.target.value })}
        size="small"
        fullWidth
        sx={{ mb: 1.5 }}
      />

      {/* Question text */}
      <TextField
        label="Question"
        value={localQuestion.question}
        onChange={(e) => handleUpdate({ question: e.target.value })}
        size="small"
        fullWidth
        multiline
        minRows={2}
        required
        sx={{ mb: 1.5 }}
      />

      {/* Details */}
      <TextField
        label="Details / Instructions"
        value={localQuestion.question_details || ''}
        onChange={(e) => handleUpdate({ question_details: e.target.value })}
        size="small"
        fullWidth
        multiline
        minRows={1}
        sx={{ mb: 1.5 }}
      />

      {/* Options for choice types */}
      {isChoiceType && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" fontWeight="bold">
            Options
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
            {localQuestion.options?.map((option, index) => (
              <Chip
                key={index}
                label={option}
                onDelete={() => handleRemoveOption(index)}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <TextField
              value={optionInput}
              onChange={(e) => setOptionInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAddOption()
                }
              }}
              placeholder="Add option"
              size="small"
              fullWidth
            />
            <IconButton
              type="button"
              onClick={handleAddOption}
              size="small"
              color="primary"
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Limits row */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          label="Char limit"
          type="number"
          value={localQuestion.char_limit || ''}
          onChange={(e) =>
            handleUpdate({
              char_limit: e.target.value
                ? parseInt(e.target.value)
                : undefined,
            })
          }
          size="small"
          sx={{ flex: 1 }}
        />
        <TextField
          label="Word limit"
          type="number"
          value={localQuestion.word_limit || ''}
          onChange={(e) =>
            handleUpdate({
              word_limit: e.target.value
                ? parseInt(e.target.value)
                : undefined,
            })
          }
          size="small"
          sx={{ flex: 1 }}
        />
      </Box>

      {/* Flags row */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={localQuestion.required ?? false}
              onChange={(e) => handleUpdate({ required: e.target.checked })}
              size="small"
            />
          }
          label={<Typography variant="body2">Required</Typography>}
          sx={{ mr: 0 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={localQuestion.attachment_required ?? false}
              onChange={(e) =>
                handleUpdate({ attachment_required: e.target.checked })
              }
              size="small"
            />
          }
          label={<Typography variant="body2">Attachment</Typography>}
          sx={{ mr: 0 }}
        />
        {isChoiceType && (
          <FormControlLabel
            control={
              <Checkbox
                checked={localQuestion.has_other_text_field || false}
                onChange={(e) =>
                  handleUpdate({ has_other_text_field: e.target.checked })
                }
                size="small"
              />
            }
            label={<Typography variant="body2">Other</Typography>}
            sx={{ mr: 0 }}
          />
        )}
      </Box>

      {/* Read-only display of conditional/dependency if present */}
      {localQuestion.conditional_on?.question_number && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Chip
            label={`Conditional: Q${localQuestion.conditional_on.question_number} ${localQuestion.conditional_on.operator || ''} ${localQuestion.conditional_on.value || ''}`}
            size="small"
            variant="outlined"
            color="info"
          />
          <IconButton
            size="small"
            onClick={() => handleUpdate({ conditional_on: undefined })}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      {localQuestion.depends_on?.question_numbers &&
        localQuestion.depends_on.question_numbers.length > 0 && (
          <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              label={`Depends on: ${localQuestion.depends_on.question_numbers.join(', ')}`}
              size="small"
              variant="outlined"
              color="info"
            />
            <IconButton
              size="small"
              onClick={() => handleUpdate({ depends_on: undefined })}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
    </Paper>
  )
}
